import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

import { EvoNode, EvoNodeVariant } from "./EvoNode";

export type EvoChainStep = {
  label: string;
  detail?: string;
  isPathway?: boolean;
  expired?: boolean;
};

export interface EvoChainProps {
  steps: EvoChainStep[];
  variant?: EvoNodeVariant;
  /** "wrap" breaks onto new rows; "scroll" keeps one row behind a scrollbar. */
  layout?: "wrap" | "scroll";
  /** "selectable" only. */
  selectedIndex?: number;
  onOpenStep?: (index: number) => void;
  onRemoveStep?: (index: number) => void;
  /** The gold dash between steps. Off inside the sheet, where it adds nothing. */
  connectors?: boolean;
  /** Fade each step in on mount, as the builder does when a step is added. */
  animate?: boolean;
  /**
   * "scroll" only. Pads the row so the scrollbar has its own lane; off in a
   * one-line bar where the chips must sit centred beside a button.
   */
  reserveScrollbarSpace?: boolean;
  className?: string;
}

/**
 * A row of EvoNodes joined by connectors. The one chain renderer: the
 * builder's Current Chain, the player page card, the players list and the
 * chain sheet all draw through it and differ only by props.
 */
export const EvoChain = ({
  steps,
  variant = "readonly",
  layout = "wrap",
  selectedIndex,
  onOpenStep,
  onRemoveStep,
  connectors = true,
  animate = false,
  reserveScrollbarSpace = true,
  className,
}: EvoChainProps) => {
  const nodes = steps.map((step, index) => (
    <ChainStep
      key={`${step.label}-${index}`}
      animate={animate}
      connector={connectors && index < steps.length - 1}
    >
      <EvoNode
        label={step.label}
        detail={step.detail}
        variant={variant}
        isPathway={step.isPathway}
        expired={step.expired}
        selected={selectedIndex === index}
        onOpen={onOpenStep && (() => onOpenStep(index))}
        onRemove={onRemoveStep && (() => onRemoveStep(index))}
        // Wrapped rows can't scroll, so a name wider than the row is clipped.
        truncate={layout === "wrap"}
      />
    </ChainStep>
  ));

  if (layout === "scroll") {
    return (
      <ScrollArea
        className={cn("w-full", className)}
        enableShadows
        blockClickOnScroll
      >
        <div
          className={cn(
            "flex flex-row items-center overflow-hidden",
            reserveScrollbarSpace && "pb-2",
          )}
        >
          {nodes}
        </div>
        <ScrollBar
          className="h-2"
          orientation="horizontal"
          scrollBarColor="bg-panel-foreground"
        />
      </ScrollArea>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-y-2", className)}>
      {nodes}
    </div>
  );
};

const ChainStep = ({
  children,
  connector,
  animate,
}: {
  children: React.ReactNode;
  connector: boolean;
  animate: boolean;
}) => {
  const [visible, setVisible] = useState(!animate);

  useEffect(() => {
    if (!animate) return;
    const timer = setTimeout(() => setVisible(true), 20);
    return () => clearTimeout(timer);
  }, [animate]);

  return (
    <div className="flex min-w-0 max-w-full items-center">
      <div
        className={cn(
          "flex min-w-0 max-w-full transition-all duration-200 ease-out",
          visible ? "scale-100 opacity-100" : "scale-95 opacity-0",
        )}
      >
        {children}
      </div>
      {connector && (
        <div
          className={cn(
            "h-0.5 w-3 shrink-0 bg-dark-primary transition-all duration-200 ease-out",
            visible ? "scale-x-100 opacity-100" : "scale-x-0 opacity-0",
          )}
        />
      )}
    </div>
  );
};
